import { queryOptions } from "@tanstack/react-query";

import { appConfigPath, appConfigSchema, type AppConfig } from "@ravnar/api-contract";

import { env } from "../env";

/** How long the app waits for its config before it goes on without one: the API keys page waits for it. */
const APP_CONFIG_WAIT_MS = 3000;

/**
 * What the stage the app is deployed to tells it (`appConfigPath`, which `FrontendStack` writes beside the build), asked
 * for as JSON: Vite's servers, which serve none, answer that with a 404, where they answer any other request for a path
 * they lack with the app's page. A config that is missing, cannot be read, makes no sense or has not come within
 * `waitMs` is the same as none: the app runs as it does on a stage that tells it nothing, so this never fails.
 */
export async function fetchAppConfig(signal?: AbortSignal, waitMs = APP_CONFIG_WAIT_MS): Promise<AppConfig> {
	const controller = new AbortController();
	const giveUp = () => {
		controller.abort();
	};
	const timer = setTimeout(giveUp, waitMs);
	signal?.addEventListener("abort", giveUp);
	try {
		const response = await fetch(new URL(appConfigPath, window.location.origin), {
			headers: { accept: "application/json" },
			signal: controller.signal,
		});
		const parsed = appConfigSchema.safeParse(response.ok ? await response.json() : undefined);
		return parsed.success ? parsed.data : {};
	} catch {
		return {};
	} finally {
		clearTimeout(timer);
		signal?.removeEventListener("abort", giveUp);
	}
}

/**
 * What the stage tells the app, asked for the first time a page needs it and not again while the answer is kept: only a
 * deploy changes it.
 */
export const appConfigQueryOptions = () =>
	queryOptions({
		queryKey: ["app-config"],
		queryFn: ({ signal }) => fetchAppConfig(signal),
		staleTime: "static",
	});

/**
 * Where software sends a request to the integration API's `path`: the API's own address where the stage tells the app
 * one (`integrationOrigin`), and otherwise where the app itself reaches the API.
 */
export function integrationUrl(path: string, config: AppConfig): string {
	return config.integrationOrigin === undefined
		? new URL(`${env.VITE_API_ORIGIN}${path}`, window.location.origin).href
		: new URL(path, config.integrationOrigin).href;
}
